import React from "react"

import type { BrokerTradeSyncInfo } from "../../utils/brokerTradeSync"
import { formatBrokerSyncDateRange } from "../../utils/brokerTradeSync"
import type { IbkrFlexPortalSetupInfo } from "../../utils/ibkrBrokerSetup"
import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_EMPHASIS_CLASS,
  BROKER_ALERT_NOTE_CLASS,
} from "./brokerAlertUi"
import BrokerGuidancePanel from "./BrokerGuidancePanel"
import {
  ChecklistRow,
  DEFAULT_IBKR_FORMAT_ITEMS,
  FormatChecklistRow,
  GuidanceAccordionBody,
  GuidanceBlock,
  GuidanceIntro,
  IbkrTimezoneFormatNote,
} from "./ibkrGuidanceShared"

export type IbkrFlexGuidanceVariant = "sync_only" | "csv_tab"

export interface IbkrFlexSyncGuidanceProps {
  tradeSync: BrokerTradeSyncInfo
  variant: IbkrFlexGuidanceVariant
  portalSetup?: IbkrFlexPortalSetupInfo | null
  compact?: boolean
}

const IbkrFlexSyncGuidance = ({
  tradeSync,
  variant,
  portalSetup,
  compact = false,
}: IbkrFlexSyncGuidanceProps): React.ReactElement => {
  const rangeLabel = formatBrokerSyncDateRange(tradeSync.syncFromDate, tradeSync.syncToDate)
  const perFileDays = tradeSync.csvMaxLookbackDaysPerFile ?? tradeSync.syncLookbackDays
  const formatItems = portalSetup?.items?.length ? portalSetup.items : DEFAULT_IBKR_FORMAT_ITEMS

  if (variant === "sync_only") {
    return (
      <BrokerGuidancePanel
        title="How to import trades via Broker Sync"
        compact={compact}
        collapsible
      >
        <GuidanceAccordionBody>
          <GuidanceIntro className="!mb-0">
            This is a one-time setup. After you save your Flex token and Query ID, Tessera syncs
            trades from your stored Activity Flex query for{" "}
            <span className={BROKER_ALERT_EMPHASIS_CLASS}>{rangeLabel}</span>. Run Sync again
            anytime to refresh recent trades.
          </GuidanceIntro>
          <p className={`${BROKER_ALERT_NOTE_CLASS} !mb-0 !mt-0`}>
            Same-day trades may take up to 24 hours to appear from IBKR. For immediate import, use
            the CSV import tab.
          </p>

          <GuidanceBlock title="1. Flex token (Client Portal)">
            <ChecklistRow className="!mb-0">
              Log in to Client Portal → Performance &amp; Reports → Flex Queries.
            </ChecklistRow>
            <ChecklistRow className="!mb-0">
              Open Flex Web Service Configuration (gear) → enable Flex Web Service → Generate new
              Flex token → Save → copy the token.
            </ChecklistRow>
          </GuidanceBlock>

          <GuidanceBlock title="2. Activity Flex Query ID (Report ID)">
            <ChecklistRow className="!mb-0">
              Click + next to Activity Flex Query → create a query (e.g. for Tessera).
            </ChecklistRow>
            <ChecklistRow className="!mb-0">
              Sections → Trades → Executions → Select all → save.
            </ChecklistRow>
            <ChecklistRow className="!mb-0">
              Set Period to the last {tradeSync.syncLookbackDays} days (max per query).
            </ChecklistRow>
            <ChecklistRow className="!mb-0">
              Open the query (info icon) and copy the Activity Flex Query ID.
            </ChecklistRow>
          </GuidanceBlock>

          <GuidanceBlock title="Required query formats">
            <p className={`${BROKER_ALERT_DESC_CLASS} !mb-0`}>
              Under General Configuration on the Activity Flex query:
            </p>
            {formatItems.map((item) => (
              <FormatChecklistRow key={item.label} item={item} />
            ))}
            {tradeSync.requiresTradeTimestampTimezone ? <IbkrTimezoneFormatNote /> : null}
          </GuidanceBlock>

          <GuidanceBlock title="3. Connect in Tessera">
            <ChecklistRow className="!mb-0">
              Enter Flex token and Flex Query ID below → Test connection → Save credentials → Sync.
            </ChecklistRow>
          </GuidanceBlock>

          <GuidanceBlock title="Common issues">
            <ChecklistRow className="!mb-0">
              Ensure there is at least one trade older than today in the account before the first
              sync.
            </ChecklistRow>
            <ChecklistRow className="!mb-0">
              If sync fails, confirm Time format includes TimeZone and matches the formats above.
            </ChecklistRow>
          </GuidanceBlock>
        </GuidanceAccordionBody>
      </BrokerGuidancePanel>
    )
  }

  return (
    <BrokerGuidancePanel title="How to import older IBKR history via CSV" compact={compact} collapsible>
      <GuidanceAccordionBody>
        <GuidanceIntro className="!mb-0">
          Export a Trade Confirmation Flex query from Client Portal, then upload each CSV below.
          Each file can cover up to {perFileDays} days. Use separate files for each year or window
          (e.g. two files for two years). When finished, use Broker Sync for recent trades (
          {rangeLabel}).
        </GuidanceIntro>

        <GuidanceBlock title="Client Portal steps">
          <ChecklistRow className="!mb-0">
            Performance &amp; Reports → Flex Queries → add a Trade Confirmation query (not
            Transaction History).
          </ChecklistRow>
          <ChecklistRow className="!mb-0">Under Executions, select all fields.</ChecklistRow>
          <ChecklistRow className="!mb-0">
            Run the query (→ icon) → pick your date range → CSV → download, then upload here.
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Required export formats">
          <p className={`${BROKER_ALERT_DESC_CLASS} !mb-0`}>
            Tessera matches these automatically:
          </p>
          {formatItems.map((item) => (
            <FormatChecklistRow key={item.label} item={item} />
          ))}
          {tradeSync.requiresTradeTimestampTimezone ? <IbkrTimezoneFormatNote /> : null}
        </GuidanceBlock>
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

export default IbkrFlexSyncGuidance
